import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorFallbackMinimal } from '@/core/ErrorFallback.organism';
import { useLicenseManagementActions } from '@/licenseManagement/licenseManagement.store';
import { getLicenseStatusFromLicense, normalizeLicenseData } from '@/licenseManagement/licenseManagement.utilities';
import { LoadingFallback } from '@/main/LoadingFallback.atom';
import { retryUnlessUnauthorized } from '@/main/apiSdk';
import { useApiGetLicenseQuery } from '@seeqdev/api/endpoint/SystemApi';
import type { ReactNode } from 'react';

export const LicenseInitialization = ({ enabled, children }: { enabled: boolean; children: ReactNode }) => {
  const { t } = useTranslation();
  const { setLicense, setLicenseStatus } = useLicenseManagementActions();
  const licenseQuery = useApiGetLicenseQuery({ enabled, retry: retryUnlessUnauthorized });
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!licenseQuery.data) return;
    setLicense(normalizeLicenseData(licenseQuery.data));
    setLicenseStatus(getLicenseStatusFromLicense(licenseQuery.data));
    setIsReady(true);
  }, [licenseQuery.data, setLicense, setLicenseStatus]);

  if (enabled && !isReady) {
    if (licenseQuery.isError && !licenseQuery.isFetching) {
      return (
        <ErrorFallbackMinimal
          error={new Error(t('LICENSE.LOAD_ERROR'))}
          retry={() => {
            void licenseQuery.refetch();
          }}
        />
      );
    }
    return <LoadingFallback />;
  }

  return children;
};
